// @ts-nocheck
/**
 * VoterDetailDrawer — right-side slide-over that shows a voter's full profile
 * when a Voters table row is clicked.
 *
 * Wiring (in VotersView):
 *   const [selectedVoterId, setSelectedVoterId] = useState<number | null>(null);
 *   <DataTable onRowClick={(row) => setSelectedVoterId(row.id)} ... />
 *   <VoterDetailDrawer
 *     voterId={selectedVoterId}
 *     onClose={() => setSelectedVoterId(null)}
 *   />
 *
 * Passing `voterId === null` keeps the drawer closed. The moment it transitions
 * to a number, the drawer fetches `/voters/{voterId}` and slides in. We keep the
 * last-loaded voter in state during the close transition so the content doesn't
 * blank out mid-slide.
 */
import React, { useEffect, useRef, useState } from 'react';
import client from '@/api/client';
import type { Voter, EngagementTier, PartySignal } from '@/types';
import { MessagingTab } from '@/components/voter-drawer/MessagingTab';
import { fetchVoterElections, fetchVoterKnockbacks } from '@/api/voters';
import { VotingRecordDots } from '@/components/VotingRecordDots';
import { findRouteStopByAddress } from '@/lib/routeLookup';

interface VoterDetailDrawerProps {
  voterId: number | null; // null = drawer closed
  onClose: () => void;
  // Track B (2026-05-17): role + current user are needed for the
  // Messaging tab's admin-only opt-in/opt-out controls. Both optional
  // so existing callers (canvass-side drawer opens) keep working
  // without modification.
  userRole?: string;
  currentUser?: { id: number };
  // Clicking the voter's address jumps to the walking route that covers it.
  // Optional so callers that aren't wired for navigation keep working.
  onJumpToRoute?: (
    subregion: string,
    shiftId: string,
    stopIndex: number,
  ) => void;
}

type DrawerTab = 'profile' | 'messaging';

// ---------------------------------------------------------------------------
// Styling helpers
// ---------------------------------------------------------------------------

const TIER_STYLES: Record<EngagementTier, { label: string; cls: string }> = {
  anchor: {
    label: 'Anchor',
    cls: 'bg-tier-anchor/20 text-tier-anchor border-tier-anchor/50',
  },
  hot: {
    label: 'Hot',
    cls: 'bg-tier-hot/20 text-tier-hot border-tier-hot/50',
  },
  warm: {
    label: 'Warm',
    cls: 'bg-tier-warm/20 text-tier-warm border-tier-warm/50',
  },
  cool: {
    label: 'Cool',
    cls: 'bg-tier-cool/40 text-tier-cool border-tier-cool/60',
  },
  super_voter: {
    label: 'Super Voter',
    cls: 'bg-status-success-bg/20 text-status-success border-status-success-bg/50',
  },
};

const PARTY_STYLES: Record<string, string> = {
  R: 'bg-status-danger-bg/20 text-status-danger border-status-danger-bg/50',
  D: 'bg-status-info-bg/20 text-status-info border-status-info-bg/50',
};

const partyClass = (p: PartySignal | undefined): string =>
  (p && PARTY_STYLES[p]) ||
  'bg-bg-elevated/60 text-fg-secondary border-border-subtle/60';

const partyLabel = (p: PartySignal | undefined): string => {
  if (!p) return '—';
  const map: Record<string, string> = {
    R: 'Republican',
    D: 'Democrat',
    I: 'Independent',
    L: 'Libertarian',
    G: 'Green',
  };
  return map[p] || p;
};

// Canvass outcome → display label + badge styling (mirrors the tier/party
// badge grammar used elsewhere in the drawer).
const OUTCOME_LABEL: Record<string, string> = {
  talked: 'Talked',
  not_home: 'Not home',
  refused: 'Refused',
  moved: 'Moved',
  come_back: 'Come back',
  do_not_contact: 'Do not contact',
};

const OUTCOME_STYLE: Record<string, string> = {
  talked:
    'bg-status-success-bg/20 text-status-success border-status-success-bg/50',
  not_home: 'bg-bg-elevated/60 text-fg-secondary border-border-subtle/60',
  refused:
    'bg-status-danger-bg/20 text-status-danger border-status-danger-bg/50',
  moved: 'bg-status-info-bg/20 text-status-info border-status-info-bg/50',
  come_back: 'bg-status-info-bg/20 text-status-info border-status-info-bg/50',
  do_not_contact:
    'bg-status-danger-bg/20 text-status-danger border-status-danger-bg/50',
};

const outcomeLabel = (o?: string | null): string =>
  (o && OUTCOME_LABEL[o]) || o || '—';
const outcomeStyle = (o?: string | null): string =>
  (o && OUTCOME_STYLE[o]) ||
  'bg-bg-elevated/60 text-fg-secondary border-border-subtle/60';

// Compact relative time, e.g. "2d ago" / "3h ago".
const relativeTime = (iso?: string | null): string => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const abs = Math.abs(diff);
  const day = 86400000;
  let s: string;
  if (abs < 3600000) s = `${Math.max(1, Math.round(abs / 60000))}m`;
  else if (abs < day) s = `${Math.round(abs / 3600000)}h`;
  else if (abs < 30 * day) s = `${Math.round(abs / day)}d`;
  else s = `${Math.round(abs / (30 * day))}mo`;
  return diff >= 0 ? `${s} ago` : `in ${s}`;
};

const formatDate = (d?: string | null): string => {
  if (!d) return '—';
  const parsed = new Date(d);
  if (Number.isNaN(parsed.getTime())) return d;
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatTimestamp = (d?: string | null): string => {
  if (!d) return '—';
  const parsed = new Date(d);
  if (Number.isNaN(parsed.getTime())) return d;
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div
    className={`bg-bg-raised border border-border-base rounded-lg p-4 ${className}`}
  >
    {children}
  </div>
);

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div>
    <div className="text-[11px] uppercase tracking-wide text-fg-muted mb-0.5">
      {label}
    </div>
    <div className="text-sm text-fg-primary">{value ?? '—'}</div>
  </div>
);

const SkeletonBlock: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse bg-bg-elevated/60 rounded ${className}`} />
);

const LoadingBody: React.FC = () => (
  <div className="space-y-4">
    <Card>
      <SkeletonBlock className="h-6 w-2/3 mb-3" />
      <SkeletonBlock className="h-4 w-1/3" />
    </Card>
    <Card>
      <div className="flex items-center gap-3">
        <SkeletonBlock className="h-6 w-20" />
        <SkeletonBlock className="h-6 w-14" />
        <SkeletonBlock className="h-10 w-14 ml-auto" />
      </div>
    </Card>
    <Card>
      <SkeletonBlock className="h-4 w-full mb-2" />
      <SkeletonBlock className="h-4 w-3/4 mb-2" />
      <SkeletonBlock className="h-4 w-1/2" />
    </Card>
    <Card>
      <div className="grid grid-cols-2 gap-4">
        <SkeletonBlock className="h-10" />
        <SkeletonBlock className="h-10" />
        <SkeletonBlock className="h-10" />
        <SkeletonBlock className="h-10" />
      </div>
    </Card>
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const VoterDetailDrawer: React.FC<VoterDetailDrawerProps> = ({
  voterId,
  onClose,
  userRole,
  currentUser,
  onJumpToRoute,
}) => {
  // Internal id lets the drawer self-navigate (e.g. clicking a household
  // member) without the parent having to re-drive `voterId`. It tracks the
  // prop, but household chips can point it at a neighbor.
  const [localVoterId, setLocalVoterId] = useState<number | null>(voterId);
  useEffect(() => {
    setLocalVoterId(voterId);
  }, [voterId]);
  const isOpen = localVoterId !== null;
  const [voter, setVoter] = useState<Voter | null>(null);
  const [elections, setElections] = useState<
    { year: number; type: 'G' | 'P' | 'S'; voted: boolean }[] | null
  >(null);
  const [household, setHousehold] = useState<any[]>([]);
  const [knockbacks, setKnockbacks] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<{
    message: string;
    status?: number;
  } | null>(null);
  const [activeTab, setActiveTab] = useState<DrawerTab>('profile');
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  // Reset to the Profile tab whenever the drawer opens on a new voter.
  // Without this, opening voter #2 after viewing voter #1's Messaging tab
  // would land you on the Messaging tab for #2 — likely not what was
  // wanted given the drawer's main role is profile review.
  useEffect(() => {
    if (localVoterId !== null) setActiveTab('profile');
  }, [localVoterId]);

  // Fetch whenever voterId changes (null → number, or number → different number)
  const fetchVoter = React.useCallback(
    async (id: number) => {
      setLoading(true);
      setError(null);
      try {
        const res = await client.get<Voter>(`/voters/${id}`);
        setVoter(res.data);
      } catch (e: any) {
        const status: number | undefined = e?.response?.status;
        const detail: string | undefined =
          e?.response?.data?.detail || e?.message;
        setError({
          message: detail || 'Failed to load voter.',
          status,
        });
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (localVoterId === null) return;
    fetchVoter(localVoterId);
  }, [localVoterId, fetchVoter]);

  // Per-election G/P/S voting record (same PII-gated call the map popup uses).
  useEffect(() => {
    if (localVoterId === null) {
      setElections(null);
      return;
    }
    let alive = true;
    setElections(null);
    fetchVoterElections(localVoterId)
      .then((r) => {
        if (alive) setElections(r.elections || []);
      })
      .catch(() => {
        if (alive) setElections([]);
      });
    return () => {
      alive = false;
    };
  }, [localVoterId]);

  // Canvass/knock history (latest outcome + count + recent). null = unknown or
  // not permitted (non-PII roles 403 here — they can't load the profile anyway).
  useEffect(() => {
    if (localVoterId === null) {
      setKnockbacks(null);
      return;
    }
    let alive = true;
    setKnockbacks(null);
    fetchVoterKnockbacks(localVoterId)
      .then((r) => {
        if (alive) setKnockbacks(r);
      })
      .catch(() => {
        if (alive) setKnockbacks(null);
      });
    return () => {
      alive = false;
    };
  }, [localVoterId]);

  // Household — other registered voters at the same address. The list endpoint
  // does a fuzzy ILIKE on `q`, so we re-filter to the exact (normalized)
  // address client-side and drop the voter themselves.
  useEffect(() => {
    const addr = voter?.address;
    const selfId = voter?.id;
    if (!addr) {
      setHousehold([]);
      return;
    }
    let alive = true;
    const key = (s: any) =>
      String(s || '')
        .toUpperCase()
        .replace(/\s+/g, ' ')
        .trim();
    client
      .get('/voters', { params: { q: addr, page_size: 50 } })
      .then((res) => {
        if (!alive) return;
        const rows = res.data?.items || [];
        setHousehold(
          rows.filter(
            (r: any) => r.id !== selfId && key(r.address) === key(addr),
          ),
        );
      })
      .catch(() => {
        if (alive) setHousehold([]);
      });
    return () => {
      alive = false;
    };
  }, [voter?.id, voter?.address]);

  // Escape key closes
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Focus the close button on open
  useEffect(() => {
    if (isOpen && closeBtnRef.current) {
      // Next tick so the element is actually visible for focus()
      const t = window.setTimeout(() => {
        closeBtnRef.current?.focus();
      }, 0);
      return () => window.clearTimeout(t);
    }
  }, [isOpen]);

  const tier = voter?.engagement_tier;
  const tierStyle = tier ? TIER_STYLES[tier] : null;
  const age =
    voter?.birth_year && voter.birth_year > 0
      ? 2026 - voter.birth_year
      : null;
  // Is this address on a walking route? If so (and a nav handler exists), the
  // address becomes a link that jumps to that route stop.
  const routeStop = voter ? findRouteStopByAddress(voter.address) : null;

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden={!isOpen}
        onClick={onClose}
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-200 ${
          isOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
      />

      {/* Drawer panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Voter details"
        aria-hidden={!isOpen}
        {...(!isOpen && { inert: '' })}
        className={`fixed inset-y-0 right-0 w-full max-w-md bg-bg-base border-l border-border-base shadow-2xl z-50 transform transition-transform duration-200 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header — name + identity (tier/party/score) read as one person card */}
        <div className="p-4 border-b border-border-base bg-bg-raised/60">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
            {loading && !voter ? (
              <SkeletonBlock className="h-7 w-48" />
            ) : voter ? (
              <h2 className="text-xl font-bold text-fg-primary truncate">
                {voter.last_name}, {voter.first_name}
              </h2>
            ) : (
              <h2 className="text-xl font-bold text-fg-primary">Voter</h2>
            )}
            {voter?.voter_id_external && (
              <div className="text-xs text-fg-muted mt-0.5 truncate">
                ID {voter.voter_id_external}
              </div>
            )}
          </div>
          <button
            ref={closeBtnRef}
            onClick={onClose}
            aria-label="Close voter details"
            className="flex-shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:text-fg-primary hover:bg-bg-elevated focus:outline-none focus:ring-2 focus:ring-status-success"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          </div>

          {/* Identity row — tier / party / score, part of the header surface */}
          {voter && (
            <div className="flex items-center gap-3 mt-3">
              {tierStyle && (
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide border ${tierStyle.cls}`}
                >
                  {tierStyle.label}
                </span>
              )}
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide border ${partyClass(
                  voter.party
                )}`}
                title={partyLabel(voter.party)}
              >
                {voter.party || 'N/A'}
              </span>
              <div className="ml-auto text-right">
                <div className="text-3xl font-bold text-status-success leading-none">
                  {Math.round(voter.score)}
                </div>
                <div className="text-[11px] uppercase tracking-wide text-fg-muted mt-1">
                  Score
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {activeTab === 'messaging' && voter && !loading && !error && (
            <MessagingTab
              voter={voter as any}
              userRole={userRole}
              currentUser={currentUser}
            />
          )}
          {activeTab === 'profile' && loading && !voter && <LoadingBody />}

          {activeTab === 'profile' && error && !loading && (
            <Card className="border-status-danger-bg/70 bg-status-danger-bg/20">
              <div className="flex items-start gap-3">
                <div className="text-status-danger font-semibold">
                  {error.status === 403
                    ? 'Access restricted'
                    : 'Could not load voter'}
                </div>
              </div>
              <p className="text-sm text-status-danger/90 mt-2">
                {error.status === 403
                  ? "Access restricted — your role can't view individual voter records."
                  : error.message}
              </p>
              {error.status !== 403 && localVoterId !== null && (
                <button
                  onClick={() => fetchVoter(localVoterId)}
                  className="mt-3 inline-flex items-center px-3 py-1.5 rounded bg-accent hover:bg-accent-hover text-white text-sm font-semibold focus:outline-none"
                >
                  Retry
                </button>
              )}
            </Card>
          )}

          {activeTab === 'profile' && voter && !loading && !error && (
            <>
              {/* Voting history — turnout at a glance, right under the score */}
              <Card>
                <div className="text-[11px] uppercase tracking-wide text-fg-muted mb-3">
                  Voting History
                </div>
                {elections && elections.length > 0 && (
                  <div className="mb-3">
                    <VotingRecordDots record={elections} max={8} />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <Field
                    label="Total Elections"
                    value={
                      <span className="text-lg font-semibold text-fg-primary">
                        {voter.election_count}
                      </span>
                    }
                  />
                  <Field
                    label="Primaries"
                    value={
                      <span className="text-lg font-semibold text-fg-primary">
                        {voter.primary_count}
                      </span>
                    }
                  />
                  <Field
                    label="Primary Voter"
                    value={
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${
                          voter.primary_voter
                            ? 'bg-status-success-bg/20 text-status-success border-status-success-bg/50'
                            : 'bg-bg-elevated/60 text-fg-secondary border-border-subtle/60'
                        }`}
                      >
                        {voter.primary_voter ? 'Yes' : 'No'}
                      </span>
                    }
                  />
                  <Field
                    label="Status"
                    value={
                      <span className="text-sm text-fg-primary">
                        {voter.status || '—'}
                      </span>
                    }
                  />
                </div>
              </Card>

              {/* Canvass status — latest knock outcome, when, and by whom.
                  Hidden until loaded so we never flash a misleading
                  "Not yet knocked" during the fetch (or on a 403). */}
              {knockbacks && (
                <Card>
                  <div className="text-[11px] uppercase tracking-wide text-fg-muted mb-2">
                    Canvass Status
                  </div>
                  {knockbacks.total_knockbacks > 0 ? (
                    <>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide border ${outcomeStyle(
                            knockbacks.latest_outcome,
                          )}`}
                        >
                          {outcomeLabel(knockbacks.latest_outcome)}
                        </span>
                        <span className="text-xs text-fg-muted">
                          {relativeTime(knockbacks.latest_timestamp)}
                        </span>
                        {knockbacks.recent_history?.[0]?.canvasser_name && (
                          <span className="text-xs text-fg-muted">
                            · by {knockbacks.recent_history[0].canvasser_name}
                          </span>
                        )}
                      </div>
                      {knockbacks.recent_history?.[0]?.notes && (
                        <p className="text-sm text-fg-secondary mt-2">
                          {knockbacks.recent_history[0].notes}
                        </p>
                      )}
                      {knockbacks.total_knockbacks > 1 && (
                        <div className="text-[11px] text-fg-muted mt-2">
                          {knockbacks.total_knockbacks} knocks total
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-sm text-fg-muted">Not yet knocked</div>
                  )}
                </Card>
              )}

              {/* 3. Location */}
              <Card>
                <div className="text-[11px] uppercase tracking-wide text-fg-muted mb-2">
                  Location
                </div>
                {routeStop && onJumpToRoute ? (
                  <button
                    type="button"
                    onClick={() => {
                      onJumpToRoute(
                        routeStop.subregion,
                        routeStop.shiftId,
                        routeStop.stopIndex,
                      );
                      onClose();
                    }}
                    title="Open this address on its walking route"
                    className="text-left text-sm font-semibold text-accent-fg hover:underline"
                  >
                    {voter.address}
                    <span aria-hidden="true"> →</span>
                  </button>
                ) : (
                  <div className="text-sm text-fg-primary">{voter.address}</div>
                )}
                <div className="text-sm text-fg-secondary">
                  {voter.city}, {voter.zip_code}
                </div>
                <div className="text-sm text-fg-muted mt-2">
                  {voter.precinct_num != null ? (
                    <>
                      P{voter.precinct_num}
                      {voter.precinct_name ? ` — ${voter.precinct_name}` : ''}
                    </>
                  ) : (
                    <span className="text-fg-muted">No precinct</span>
                  )}
                </div>
                {voter.legislative_district && (
                  <div className="text-sm text-fg-muted">
                    LD {voter.legislative_district}
                  </div>
                )}
              </Card>

              {/* 3b. Household — others at this address (self-navigating) */}
              {household.length > 0 && (
                <Card>
                  <div className="text-[11px] uppercase tracking-wide text-fg-muted mb-2">
                    Others at this address ({household.length})
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {household.map((m: any) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setLocalVoterId(m.id)}
                        title="View this voter"
                        className="inline-flex items-center gap-1.5 rounded-full border border-border-base bg-bg-elevated px-3 py-1 text-sm font-semibold text-fg-primary hover:border-accent/60"
                      >
                        {m.first_name} {m.last_name}
                        {m.engagement_tier && (
                          <span className="text-[10px] uppercase tracking-wide text-fg-muted">
                            {m.engagement_tier}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </Card>
              )}

              {/* 5. Demographics */}
              <Card>
                <div className="text-[11px] uppercase tracking-wide text-fg-muted mb-3">
                  Demographics
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field
                    label="Registered"
                    value={formatDate(voter.registration_date)}
                  />
                  <Field
                    label="Birth Year"
                    value={voter.birth_year ?? '—'}
                  />
                  <Field label="Age" value={age ?? '—'} />
                </div>
              </Card>

              {/* 6. Footer timestamps */}
              <div className="text-[11px] text-fg-muted flex flex-col gap-0.5 px-1 pb-2">
                <div>Created: {formatTimestamp(voter.created_at)}</div>
                <div>Updated: {formatTimestamp(voter.updated_at)}</div>
              </div>
            </>
          )}
        </div>

        {/* Bottom tab bar — section switcher lives at the bottom so the
            profile content (score, turnout, location) leads. */}
        <div
          className="flex-shrink-0 flex gap-2 p-2 border-t border-border-base bg-bg-raised/60"
          role="tablist"
          aria-label="Voter detail sections"
        >
          <button
            role="tab"
            aria-selected={activeTab === 'profile'}
            onClick={() => setActiveTab('profile')}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-bold transition ${
              activeTab === 'profile'
                ? 'bg-status-success-bg text-white'
                : 'text-fg-secondary hover:bg-bg-elevated'
            }`}
          >
            Profile
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'messaging'}
            onClick={() => setActiveTab('messaging')}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-bold transition ${
              activeTab === 'messaging'
                ? 'bg-status-success-bg text-white'
                : 'text-fg-secondary hover:bg-bg-elevated'
            }`}
          >
            Messaging
          </button>
        </div>
      </aside>
    </>
  );
};

export default VoterDetailDrawer;
